import { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ASO_API, downloadAsoReportPdf, downloadAsoReportSummary } from "@/lib/asoEngine";

const companySizes = ["1-10", "11-50", "51-200", "201-500", "500+"];
const installBrackets = ["<10k", "10k-50k", "50k-200k", "200k-1M", "1M+"];

export const AsoLeadCaptureGate = ({ report, onQualified }) => {
  const [form, setForm] = useState({ phone_number: report.qualification?.phone_number || "", company_size: report.qualification?.company_size || "", monthly_installs: report.qualification?.monthly_installs || "" });
  const [submitting, setSubmitting] = useState(false);

  const submitGate = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const response = await axios.post(`${ASO_API}/reports/${report.id}/consultation`, form);
      toast.success("Consultation request saved. Equinox will reach out with scheduling details.");
      onQualified?.(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to save the consultation request right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="px-6 pb-20 pt-8 sm:px-8 lg:px-12 lg:pb-28" data-testid="aso-lead-capture-section">
      <div className="mx-auto max-w-7xl rounded-[2.4rem] border border-white/[0.08] bg-white/[0.04] p-6 backdrop-blur-2xl lg:p-8">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="section-kicker">Consultation unlock</p>
            <h2 className="mt-5 max-w-[12ch] font-display text-3xl tracking-[-0.05em] text-white sm:text-4xl">Share a few details before downloading the founder summary.</h2>
            <p className="mt-5 max-w-xl text-sm leading-8 text-white/[0.64]">
              Calendly goes live later. For now, we’ll save your report lead, unlock the summary, and our team will coordinate the consultation manually.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.2em] text-white/[0.5]" data-testid="aso-download-options-copy">
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">Full PDF audit</span>
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">TXT founder summary</span>
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">Unlocks after consultation gate</span>
            </div>
          </div>

          <form onSubmit={submitGate} className="editorial-shell premium-border glass-panel rounded-[2rem] p-5 lg:p-6" data-testid="aso-lead-capture-form">
            <div className="grid gap-4 md:grid-cols-3">
              <label className="block text-sm text-white/[0.76] md:col-span-1">
                <span>Phone Number</span>
                <input
                  value={form.phone_number}
                  onChange={(event) => setForm((current) => ({ ...current, phone_number: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="aso-phone-number-input"
                />
              </label>

              <label className="block text-sm text-white/[0.76] md:col-span-1">
                <span>Company Size</span>
                <select
                  value={form.company_size}
                  onChange={(event) => setForm((current) => ({ ...current, company_size: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="aso-company-size-input"
                >
                  <option value="">Select</option>
                  {companySizes.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>

              <label className="block text-sm text-white/[0.76] md:col-span-1">
                <span>Monthly App Installs</span>
                <select
                  value={form.monthly_installs}
                  onChange={(event) => setForm((current) => ({ ...current, monthly_installs: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="aso-monthly-installs-input"
                >
                  <option value="">Select</option>
                  {installBrackets.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
            </div>

            <div className="mt-6 flex flex-col gap-3 xl:flex-row xl:items-center">
              <button
                type="submit"
                disabled={submitting}
                className="glass-button glass-panel rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="aso-book-consultation-button"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {submitting ? "Saving lead..." : "Book Free ASO Consultation"}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </button>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => downloadAsoReportPdf(report)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
                  data-testid="aso-download-pdf-button"
                  disabled={!report.qualification?.phone_number}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Download Full PDF Audit
                </button>

                <button
                  type="button"
                  onClick={() => downloadAsoReportSummary(report)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
                  data-testid="aso-download-summary-button"
                  disabled={!report.qualification?.phone_number}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Download TXT Summary
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};